import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { cloneElement, useCallback, useRef } from "react";
import { elevation } from "@m/alchemy-theme";
import { createPortal } from "react-dom";
import FocusTrap from "focus-trap-react";
import { usePortalRoot } from "../portal-root/PortalRootContext";
import { combineRefs } from "../combine-refs";
import ClickAwayListener from "../vendor/mui-base/ClickAwayListener";
import { usePopperState } from "./usePopperState";
import { usePopperJs } from "./usePopperJs";
import { usePopperA11y } from "./usePopperA11y";
import { useOverlayEvents } from "./useOverlayEvents";
import { useTriggerEvents } from "./useTriggerEvents";
import { PopperSpring } from "./PopperSpring";
import * as Styled from "./Popper.styled";
import { PortalFocusWrapper } from "./PortalFocusWrapper";
import { MotionState } from "./types";
import { useRestoreFocusOnReopen } from "./useRestoreFocusOnReopen";
export const Popper = ({ overlay, overlayPointerEvents, trigger, triggerRef: triggerRefProp, placementRef, popoverOptions, tooltipOptions, targetElement, mode, onOpen, onClose, onOpened, onClosed, onOpenCallback, onCloseCallback, shouldFollowCursor, shouldMatchWidth, isOpen: isOpenProp, overlayOffset, shouldCloseOnScroll, placement, fallbackPlacements, zIndex = elevation("modal"), stringMap, 
// deprecated props
returnFocusRef: returnFocusRefProp, shouldCloseOnOutsideInteraction: shouldCloseOnOutsideInteractionProp, // should it still apply to tooltips?
shouldNeverThrowFocus, delay: delayProp, isSilent, 
// @ts-expect-error -- intentionally not adding to prop types but still allowing this for known edge cases and unit tests
__suppressAriaSupportCheck }) => {
    const { closeLabel } = stringMap;
    const portalRoot = usePortalRoot();
    const triggerRefInternal = useRef();
    const triggerRef = triggerRefProp ?? triggerRefInternal;
    const positionerRef = useRef(null);
    const overlayRef = useRef();
    const { delay, mode: tooltipMode } = mode === "tooltip"
        ? {
            delay: delayProp ?? 0,
            mode: isSilent ? "silent" : "description",
            ...tooltipOptions
        }
        : { delay: undefined, mode: undefined };
    const { mode: popoverMode, labelId: popoverLabelId, descriptionId: popoverDescriptionId, returnFocusRef, shouldCloseOnOutsideInteraction } = mode === "popover"
        ? {
            mode: shouldNeverThrowFocus ? "manual" : "auto", // TODO: should shouldCloseOnOutsideInteraction also be required for manual mode?
            returnFocusRef: returnFocusRefProp,
            shouldCloseOnOutsideInteraction: shouldCloseOnOutsideInteractionProp,
            ...popoverOptions
        }
        : {
            labelId: undefined,
            descriptionId: undefined,
            mode: undefined,
            returnFocusRef: undefined,
            shouldCloseOnOutsideInteraction: true // for tooltips, always on
        };
    const isLegacyManualMode = popoverMode === "manual" && !shouldNeverThrowFocus;
    const { close, clearClose, closeOnIntent, motionState, setMotionState, isOpen, isTouch, openOnDelay, setIsOpen, toggle } = usePopperState({
        delay,
        isOpenProp,
        onOpen,
        onClose,
        onCloseCallback,
        onOpenCallback,
        onOpened,
        onClosed,
        shouldCloseOnScroll
    });
    const getRestoreFocusTarget = useCallback((fallbackFocus) => returnFocusRef?.current ?? triggerRef?.current ?? fallbackFocus, [returnFocusRef, triggerRef]);
    const closeAndReturnFocus = useCallback((e) => {
        close(e);
        const focusTarget = returnFocusRef?.current ?? triggerRef?.current;
        // Dialog returns focus itself when closing
        const shouldReturnFocus = 
        // FocusTrap returns focus itself when closing but only if it's active (not in the process of opening)
        (popoverMode === "dialog" && motionState === MotionState.Opening) ||
            popoverMode === "auto" ||
            isLegacyManualMode;
        if (shouldReturnFocus) {
            focusTarget?.focus();
        }
    }, [close, returnFocusRef, triggerRef, popoverMode, motionState, isLegacyManualMode]);
    const triggerReactElement = typeof trigger === "function" ? trigger({ motionState, isOpen, setIsOpen }) : trigger;
    const overlayReactElement = typeof overlay === "function"
        ? overlay({ isOpen, motionState, setIsOpen, overlayRef, closeAndReturnFocus, close: closeOnIntent })
        : overlay;
    const { closeOnOutsideInteraction, overlayId, overlayRole, shouldPortalTooltipAriaRegion, tooltipAriaRegion, triggerProps } = usePopperA11y({
        close,
        closeAndReturnFocus,
        isOpen,
        shouldCloseOnOutsideInteraction,
        mode,
        popoverMode,
        tooltipMode,
        portalRoot,
        openOnDelay,
        overlayReactElement,
        overlayRef,
        motionState,
        triggerReactElement,
        triggerRef: 
        // internal triggerRef is useless to us if it's going to be empty all the time
        triggerRefProp ?? triggerReactElement ? triggerRef : undefined,
        suppressAriaSupportCheck: __suppressAriaSupportCheck
    });
    const triggerElement = (() => {
        if (!triggerReactElement) {
            return;
        }
        return cloneElement(triggerReactElement, {
            ref: triggerReactElement.ref ? combineRefs(triggerRef, triggerReactElement.ref) : triggerRef,
            ...triggerProps
        });
    })();
    const { hasArrow } = overlayReactElement.props;
    useTriggerEvents({
        close,
        closeOnIntent,
        isTouch,
        mode,
        openOnDelay,
        toggle,
        triggerRef
    });
    useOverlayEvents({
        closeOnIntent,
        isTouch,
        mode,
        clearClose,
        positionerRef,
        shouldCloseOnOutsideInteraction
    });
    const { current: popper } = usePopperJs({
        hasArrow,
        isOpen: isOpen || motionState !== MotionState.Closed,
        isTouch,
        overlayOffset,
        positionerRef,
        placement,
        fallbackPlacements,
        shouldFollowCursor,
        overlayRef,
        targetElement,
        placementRef: placementRef ?? triggerRef
    });
    const animate = (content) => (_jsx(PopperSpring, { tabIndex: mode === "popover" ? -1 : undefined, id: mode === "popover" ? overlayId : undefined, role: overlayRole, "aria-modal": popoverMode === "dialog" || undefined, "aria-labelledby": popoverMode === "dialog" ? popoverLabelId : undefined, "aria-describedby": popoverMode === "dialog" ? popoverDescriptionId : undefined, ref: overlayRef, isOpen: isOpen, setMotionState: setMotionState, placement: popper?.state.placement ?? placement, motionState: motionState, children: content }));
    const renderOverlay = () => {
        if (popoverMode === "dialog") {
            return (_jsx(FocusTrap
            // FIXME: This prevents closeAndReturnFocus from working properly
            , { 
                // FIXME: This prevents closeAndReturnFocus from working properly
                active: motionState === "opened", focusTrapOptions: {
                    fallbackFocus: `#${overlayId}`,
                    initialFocus: false,
                    clickOutsideDeactivates: true,
                    returnFocusOnDeactivate: true,
                    escapeDeactivates: false,
                    // https://github.com/focus-trap/focus-trap-react#testing-in-jsdom
                    tabbableOptions: { displayCheck: "none" },
                    setReturnFocus: getRestoreFocusTarget
                }, children: animate(overlayReactElement) }));
        }
        // Legacy manual mode (shouldNeverThrowFocus) still relies on the focus wrapper being available
        if (popoverMode === "auto" || isLegacyManualMode) {
            return (_jsx(PortalFocusWrapper, { handleClose: closeAndReturnFocus, closeLabel: closeLabel, children: animate(overlayReactElement) }));
        }
        // manual popover mode or tooltip
        return animate(overlayReactElement);
    };
    useRestoreFocusOnReopen({ motionState, positionerRef, isEnabled: mode === "popover" && popoverMode !== "manual" });
    return (_jsxs(_Fragment, { children: [triggerElement, shouldPortalTooltipAriaRegion && portalRoot !== null
                ? createPortal(tooltipAriaRegion, portalRoot) // used to render label or live region
                : tooltipAriaRegion // used to render description
            , portalRoot !== null &&
                (isOpen || motionState !== MotionState.Closed) &&
                // TODO: revisit after HTML popover becomes more widely supported
                createPortal(_jsx(ClickAwayListener, { onClickAway: closeOnOutsideInteraction, children: _jsx(Styled.Positioner, { "data-testid": "PopperPositioner", overlayPointerEvents: overlayPointerEvents, shouldFollowCursor: shouldFollowCursor, isSafeToTransition: motionState === MotionState.Opened && !shouldFollowCursor && Boolean(targetElement), shouldMatchWidth: shouldMatchWidth, minWidth: triggerRef?.current?.clientWidth, zIndex: zIndex, ref: positionerRef, children: renderOverlay() }) }), portalRoot)] }));
};
Popper.defaultProps = {
    mode: "popover",
    placement: "auto",
    canTransitionPosition: true,
    shouldCloseOnOutsideInteraction: true,
    shouldFollowCursor: false,
    strategy: "absolute",
    zIndex: elevation("modal"),
    stringMap: {
        closeLabel: "close"
    }
};
Popper.displayName = "Popper";
